/**
 * ComparisonTab — sheet "Comparison": so sánh current PO với comparison PO
 * (spec §6 + `plan/production/BATCH_REPORT_COMPARISON_DATA_SPEC.md`).
 *
 * Data 100% LIVE từ `GET /factories/{fc}/batch-report/{po}/comparison?comparison_po_no=`
 * (aggregation 2 view + reuse Production Analysis). KHÔNG mock/dummy
 * ([[feedback_api_view_source_of_truth]]): chưa chọn PO/comparison → empty-state;
 * loading → spinner; lỗi → Alert.
 *
 * Style BOM (block accent bar + số kế toán NumText/PctText) + bảng dữ liệu theo
 * skill `/table-style` đầy đủ: FilterBar Search global + per-column filter ▽ + sort
 * click header + resize width (kéo handle) + wrap-on-resize auto-height (§7.8).
 * Component dùng chung `FilterTable` cho cả 3 bảng.
 *
 * Sections (đúng workbook):
 *   ① Yield comparison by item (prev vs cur %, variance) + dòng TOTAL.
 *   ② FAQ quality comparison (12 chỉ tiêu input-side, 3 dòng prev/cur/Variance).
 *   ④ Price info (LIFFE/Diff + Premium vs G2 theo item) + ⑤ Production info.
 *
 * (③ "Finished product quality after processing" đã chuyển sang tab Production
 *  Analysis 2026-06-29, đặt cùng block "Quality information".)
 */
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { type ReactNode } from 'react'

import {
  useGetBatchReportComparisonQuery,
  type BatchReportComparison,
  type FaqQualityRow,
} from '../../../../store/api/factoryBomApi'
import { fmtMt, fmtPlain, fmtUsd, fmtUsdPrecise, fmtVarPct, formatAccessDate } from './formatters'
import { BlockTitle, NumText, PctText } from './reportUi'
import {
  COLORS,
  neutralChipSx,
  sectionCardSx,
  sectionHeaderSx,
  sectionSubtitleSx,
  sectionTitleSx,
  varianceColor,
} from './styleTokens'
import { FilterTable, type FilterCol } from './FilterTable'

interface Props {
  factoryCode: string
  poNo: string
  /** PO list (Date filter chung) để chọn comparison PO. */
  poOptions: string[]
  poMeta: Record<string, { bomCode: string; poDate: string }>
  /** Comparison PO (controlled — lift lên BatchReportPage để Export dùng chung). */
  cmpPo: string
  onCmpPoChange: (po: string) => void
}


const totalCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: COLORS.bgSubtle,
  borderTop: `1px solid ${COLORS.borderStrong}`,
  borderBottom: 'none',
} as const

/** Section header kiểu BOM: BlockTitle (accent bar) trái + caption/right phải. */
function SectionHead({ title, caption, right }: { title: string; caption?: string; right?: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
      {/* minWidth:0 → title co lại & wrap giữa các từ khi hẹp (BlockTitle giữ nguyên format BOM). */}
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        <BlockTitle>{title}</BlockTitle>
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
        {caption ? <Typography sx={{ ...sectionSubtitleSx, mb: 0 }}>{caption}</Typography> : null}
        {right}
      </Stack>
    </Stack>
  )
}

/* ---------- Section ①: Yield comparison by item ------------------------- */

type YieldRow = BatchReportComparison['yieldComparison'][number]

/**
 * Diverging data bar cho cột Variance (Excel col H = in-cell chart sau Var):
 * thanh căn giữa trục 0 — Var dương → bar xanh sang phải, âm → đỏ sang trái.
 * Scale RESPONSIVE theo data: |Var| lớn nhất bảng (`maxAbs`) đầy nửa ô, item khác
 * scale theo → không cần mốc cứng, item lệch nhất luôn full.
 */
function VarianceBar({ value, maxAbs }: { value: number; maxAbs: number }) {
  const half = maxAbs > 0 ? Math.min(50, (Math.abs(value) / maxAbs) * 50) : 0
  const positive = value > 0
  const color = positive ? COLORS.success : COLORS.danger
  return (
    <Box sx={{ position: 'relative', height: 26, width: '100%', minWidth: 22, display: 'flex', alignItems: 'center' }}>
      {/* trục 0 — vạch mảnh dịu */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '1px',
          height: 22,
          bgcolor: COLORS.border,
        }}
      />
      {/* thanh phẳng mỏng, màu đặc, hình chữ nhật vuông góc, mọc từ trục */}
      {value !== 0 && half > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            height: 13,
            borderRadius: 0,
            bgcolor: color,
            width: `${half}%`,
            ...(positive ? { left: '50%' } : { right: '50%' }),
          }}
        />
      ) : null}
    </Box>
  )
}

function YieldComparison({ rows, curPo, cmpPo, headerRight }: { rows: YieldRow[]; curPo: string; cmpPo: string; headerRight?: ReactNode }) {
  // Scale responsive: |Var| lớn nhất bảng → bar item lệch nhất đầy nửa ô.
  const maxAbsVar = rows.reduce((m, r) => Math.max(m, Math.abs(r.variance || 0)), 0)
  const cols: FilterCol<YieldRow>[] = [
    { key: 'item', label: 'Item', width: 110, frozen: true, value: (r) => r.item },
    { key: 'category', label: 'Category', width: 120, filter: 'checkboxes', value: (r) => r.category },
    { key: 'premium', label: 'Premium', width: 96, align: 'right', numeric: true, value: (r) => r.premium, render: (r) => <NumText n={r.premium} d={2} usd /> },
    // Title 2 cột = PO No: cột "previous" = Compare-with PO, "current" = PO đang chọn.
    // Đổi theo PO/Compare-with; width đủ rộng để hiện full PO No.
    { key: 'previous', label: cmpPo || '—', width: 150, align: 'right', numeric: true, value: (r) => r.previous, render: (r) => <PctText frac={r.previous} d={2} /> },
    { key: 'current', label: curPo || '—', width: 150, align: 'right', numeric: true, value: (r) => r.current, render: (r) => <PctText frac={r.current} d={2} /> },
    {
      key: 'variance',
      label: 'Var.',
      width: 100,
      align: 'right',
      numeric: true,
      value: (r) => r.variance,
      render: (r) => (
        <Box component="span" sx={{ color: varianceColor(r.variance), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {fmtVarPct(r.variance, 2)}
        </Box>
      ),
    },
    // Cột chart (Excel col H): diverging data bar trực quan hoá Variance. Sort theo
    // Var (numeric) nhưng KHÔNG filter (filter: 'none').
    {
      key: 'varChart',
      label: 'Chart',
      width: 40,
      numeric: true,
      filter: 'none',
      value: (r) => r.variance,
      render: (r) => <VarianceBar value={r.variance} maxAbs={maxAbsVar} />,
    },
  ]
  return (
    <Box>
      <SectionHead title="Batch out-turn comparison" right={headerRight} />
      <FilterTable
        storageKey="comparison.yield"
        columns={cols}
        rows={rows}
        rowKey={(r) => r.item}
        searchPlaceholder="Search items…"
        footer={(drows) => {
          const t = drows.reduce(
            (a, r) => ({ previous: a.previous + r.previous, current: a.current + r.current, variance: a.variance + r.variance }),
            { previous: 0, current: 0, variance: 0 },
          )
          return (
            <TableRow>
              <TableCell sx={totalCellSx}>TOTAL</TableCell>
              <TableCell sx={totalCellSx} />
              <TableCell sx={totalCellSx} />
              <TableCell align="right" sx={totalCellSx}><PctText frac={t.previous} d={2} /></TableCell>
              <TableCell align="right" sx={totalCellSx}><PctText frac={t.current} d={2} /></TableCell>
              <TableCell align="right" sx={{ ...totalCellSx, color: varianceColor(t.variance), fontVariantNumeric: 'tabular-nums' }}>
                {fmtVarPct(t.variance, 2)}
              </TableCell>
              <TableCell sx={totalCellSx} />
            </TableRow>
          )
        }}
      />
    </Box>
  )
}

/* ---------- Section ②: FAQ quality comparison --------------------------- */

function FaqQualityCompare({ rows }: { rows: FaqQualityRow[] }) {
  const qcols: { key: keyof FaqQualityRow; label: string }[] = [
    { key: 'mc', label: 'MC' },
    { key: 'blacks', label: 'Blacks' },
    { key: 'brokens', label: 'Brokens' },
    { key: 'bb', label: 'BB' },
    { key: 'fm', label: 'FM' },
    { key: 'mouldy', label: 'Mouldy' },
    { key: 'brown', label: 'Brown' },
    { key: 'partlyBlack', label: 'Partly Black' },
    { key: 'immature', label: 'Immature' },
    { key: 's18', label: 'S18' },
    { key: 's16', label: 'S16' },
    { key: 's13', label: 'S13' },
  ]
  const cols: FilterCol<FaqQualityRow>[] = [
    {
      key: 'po',
      label: 'PO',
      width: 180,
      frozen: true,
      value: (r) => r.po,
      render: (r) => (
        <Box component="span" className="app-table-cell-wrap" sx={{ fontWeight: r.po === 'Variance' ? 700 : 600 }}>
          {r.po}
        </Box>
      ),
    },
    ...qcols.map<FilterCol<FaqQualityRow>>((c) => ({
      key: String(c.key),
      label: c.label,
      width: 92,
      align: 'right',
      numeric: true,
      value: (r) => Number(r[c.key]) || 0,
      render: (r) => {
        const v = Number(r[c.key]) || 0
        return r.po === 'Variance' ? (
          <Box component="span" sx={{ color: varianceColor(v), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {(v >= 0 ? '+' : '') + fmtPlain(v, 2)}
          </Box>
        ) : (
          <NumText n={v} d={2} />
        )
      },
    })),
  ]
  return (
    <Box>
      <SectionHead title="FAQ quality comparison" />
      <FilterTable storageKey="comparison.faq" columns={cols} rows={rows} rowKey={(r) => r.po} searchPlaceholder="Search…" />
    </Box>
  )
}

/* ---------- Section ④/⑤: Price info + Production info -------------------- */

// Cùng format Cost Breakdown (BomTab): label nhỏ trái / value đậm phải, nowrap.
const PP_LABEL_FS = '0.72rem'
const PP_VALUE_FS = '0.7rem'

type PriceInfo = BatchReportComparison['priceInfo']
type ProdInfo = BatchReportComparison['productionInfo']
type PnlInfo = BatchReportComparison['pnlInfo']
type PpCell = { text: string; tone?: 'danger' | 'success' }

const EMPTY_PRICE: PriceInfo = { liffe: null, g2Diffs: 0, diffLabel: '', priceDate: null, premiumByItem: [] }
const EMPTY_PROD: ProdInfo = { totalInput: 0, totalOutturnMt: 0, loss: 0, mcLoss: 0, unexplainedLoss: 0 }
const EMPTY_PNL: PnlInfo = {
  valueIn: 0, valueOut: 0, valueExtracted: 0, valueExtractedPerMt: 0, handling: 0,
  valueExtractedPerMtAfterCost: 0, actualExtracted: 0, bomExtracted: 0, pnlPerMt: 0, totalPnl: 0,
}

const ppToneColor = (tone?: 'danger' | 'success') =>
  tone === 'danger' ? COLORS.danger : tone === 'success' ? COLORS.success : COLORS.text

/** Block ④/⑤ — Price & Production so sánh cả 2 PO (comparison ‖ current) cạnh nhau.
 *  Chưa chọn comparison PO → chỉ hiện 1 cột (current). */
function PriceProductionPanels({
  price,
  prod,
  pnl,
  priceCmp,
  prodCmp,
  pnlCmp,
  curPo,
  cmpPo,
  headerRight,
}: {
  price: PriceInfo
  prod: ProdInfo
  pnl: PnlInfo
  priceCmp: PriceInfo
  prodCmp: ProdInfo
  pnlCmp: PnlInfo
  curPo: string
  cmpPo: string
  headerRight?: ReactNode
}) {
  // Defensive: BE cũ (chưa restart) chưa trả *Compare → fallback empty, không crash.
  price = price ?? EMPTY_PRICE
  prod = prod ?? EMPTY_PROD
  pnl = pnl ?? EMPTY_PNL
  priceCmp = priceCmp ?? EMPTY_PRICE
  prodCmp = prodCmp ?? EMPTY_PROD
  pnlCmp = pnlCmp ?? EMPTY_PNL
  const hasCmp = !!cmpPo
  // Union item Premium — giữ thứ tự current trước, comparison bổ sung phần thiếu.
  const premItems: string[] = []
  price.premiumByItem.forEach((p) => { if (!premItems.includes(p.item)) premItems.push(p.item) })
  priceCmp.premiumByItem.forEach((p) => { if (!premItems.includes(p.item)) premItems.push(p.item) })
  const curPrem = new Map(price.premiumByItem.map((p) => [p.item, p.premium]))
  const cmpPrem = new Map(priceCmp.premiumByItem.map((p) => [p.item, p.premium]))

  const usd = (n: number | null | undefined): PpCell => ({ text: n == null ? '—' : fmtUsd(n) })
  // P&L info: USD 2 chữ số thập phân (chị Đẹp).
  const usd2 = (n: number | null | undefined): PpCell => ({ text: n == null ? '—' : fmtUsdPrecise(n) })
  const diffCell = (p: PriceInfo): PpCell => ({ text: p.diffLabel || (p.g2Diffs ? fmtUsd(p.g2Diffs) : '—') })
  const mt = (n: number): PpCell => ({ text: `${fmtMt(n)} Mt` })
  // Số đen hết (chị Đẹp) — chỉ dòng "Value extracted /Mt -costs" (strong) tô copper.
  const lossCell = (n: number): PpCell => ({ text: fmtVarPct(n, 2) })

  return (
    <Box>
      <Box sx={{ mb: 1.25 }}>
        <SectionHead title="P&L, price, production info" right={headerRight} />
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        {/* ⑥ P&L info (Extracted value) — đưa lên đầu block */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 340px', minWidth: 300 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>P&amp;L info</Typography>
            <Chip size="small" label="USD" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <PpHead curPo={curPo} cmpPo={cmpPo} hasCmp={hasCmp} />
            <PpRow label="Value in" cmp={usd2(pnlCmp.valueIn)} cur={usd2(pnl.valueIn)} hasCmp={hasCmp} />
            <PpRow label="Value out" cmp={usd2(pnlCmp.valueOut)} cur={usd2(pnl.valueOut)} hasCmp={hasCmp} />
            <PpRow label="Value extracted" cmp={usd2(pnlCmp.valueExtracted)} cur={usd2(pnl.valueExtracted)} hasCmp={hasCmp} />
            <PpRow label="Value extracted /Mt" cmp={usd2(pnlCmp.valueExtractedPerMt)} cur={usd2(pnl.valueExtractedPerMt)} hasCmp={hasCmp} />
            <PpRow label="Handling" cmp={usd2(pnlCmp.handling)} cur={usd2(pnl.handling)} hasCmp={hasCmp} />
            <PpRow label="Value extracted /Mt -costs" cmp={usd2(pnlCmp.valueExtractedPerMtAfterCost)} cur={usd2(pnl.valueExtractedPerMtAfterCost)} hasCmp={hasCmp} strong />
          </Box>
        </Box>
        {/* ④ Price info */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 340px', minWidth: 300 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Price info</Typography>
            <Chip size="small" label="USD" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <PpHead curPo={curPo} cmpPo={cmpPo} hasCmp={hasCmp} />
            <PpRow label="LIFFE" cmp={usd(priceCmp.liffe)} cur={usd(price.liffe)} hasCmp={hasCmp} />
            <PpRow label="Diffs" cmp={diffCell(priceCmp)} cur={diffCell(price)} hasCmp={hasCmp} />
            {price.priceDate || priceCmp.priceDate ? (
              <PpRow
                label="Price date"
                cmp={{ text: priceCmp.priceDate ? formatAccessDate(priceCmp.priceDate) : '—' }}
                cur={{ text: price.priceDate ? formatAccessDate(price.priceDate) : '—' }}
                hasCmp={hasCmp}
              />
            ) : null}
            <PpGroupLabel>Premium vs G2 (graded)</PpGroupLabel>
            {premItems.length === 0 ? (
              <Typography sx={{ fontSize: PP_LABEL_FS, color: COLORS.textMuted, mt: 0.5 }}>—</Typography>
            ) : (
              premItems.map((it) => (
                <PpRow
                  key={it}
                  label={it}
                  cmp={{ text: cmpPrem.has(it) ? fmtUsd(cmpPrem.get(it)!) : '—' }}
                  cur={{ text: curPrem.has(it) ? fmtUsd(curPrem.get(it)!) : '—' }}
                  hasCmp={hasCmp}
                />
              ))
            )}
          </Box>
        </Box>
        {/* ⑤ Production info */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 340px', minWidth: 300 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Production info</Typography>
            <Chip size="small" label="Mt / %" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <PpHead curPo={curPo} cmpPo={cmpPo} hasCmp={hasCmp} />
            <PpRow label="Total input" cmp={mt(prodCmp.totalInput)} cur={mt(prod.totalInput)} hasCmp={hasCmp} />
            {/* Dòng kết quả nhấn (border-top) — mirror ResultRow của Cost Breakdown. */}
            <PpRow
              label="Total out-turn"
              cmp={{ text: `${fmtMt(prodCmp.totalOutturnMt)} Mt` }}
              cur={{ text: `${fmtMt(prod.totalOutturnMt)} Mt` }}
              hasCmp={hasCmp}
            />
            <PpGroupLabel>Loss breakdown</PpGroupLabel>
            {/* Số có dấu (+/−) theo chuẩn fmtVarPct; âm = giảm = xanh. */}
            <PpRow label="Loss" cmp={lossCell(prodCmp.loss)} cur={lossCell(prod.loss)} hasCmp={hasCmp} />
            <PpRow label="MC loss" cmp={lossCell(prodCmp.mcLoss)} cur={lossCell(prod.mcLoss)} hasCmp={hasCmp} />
            <PpRow label="Unexpl. loss" cmp={lossCell(prodCmp.unexplainedLoss)} cur={lossCell(prod.unexplainedLoss)} hasCmp={hasCmp} />
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}

// Cột value hẹp lại trên mobile để dành chỗ cho label (label tự wrap, không bị che).
const ppGridCols = (hasCmp: boolean) => ({
  xs: hasCmp ? '1fr 76px 76px' : '1fr 84px',
  sm: hasCmp ? '1fr 104px 104px' : '1fr 104px',
})

/* Header 2 PO: cột comparison (trái) ‖ current (phải). */
function PpHead({ curPo, cmpPo, hasCmp }: { curPo: string; cmpPo: string; hasCmp: boolean }) {
  const headSx = {
    fontSize: '0.64rem',
    fontWeight: 800,
    color: COLORS.textMuted,
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: ppGridCols(hasCmp), columnGap: 1.5, pb: 0.4, mb: 0.2, borderBottom: `1px solid ${COLORS.border}` }}>
      <span />
      {hasCmp ? <Typography sx={headSx}>{cmpPo || '—'}</Typography> : null}
      <Typography sx={headSx}>{curPo || '—'}</Typography>
    </Box>
  )
}

/* Dòng label + (comparison) + current — mirror CostResult của BomTab. */
function PpRow({ label, cmp, cur, hasCmp, strong }: { label: string; cmp: PpCell; cur: PpCell; hasCmp: boolean; strong?: boolean }) {
  // Dòng nhấn mạnh = mirror ResultRow của BomTab: bold + màu copper (warn) +
  // borderTop; tone lãi/lỗ (nếu có) thắng màu copper.
  const valSx = (tone?: 'danger' | 'success') => ({
    fontSize: PP_VALUE_FS,
    fontWeight: 800,
    color: tone ? ppToneColor(tone) : strong ? COLORS.warnStrong : COLORS.text,
    whiteSpace: 'nowrap' as const,
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums' as const,
  })
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: ppGridCols(hasCmp),
        columnGap: 1.5,
        alignItems: 'baseline',
        mt: strong ? 0.75 : 0.5,
        ...(strong ? { pt: 0.75, borderTop: `1px solid ${COLORS.borderStrong}` } : {}),
      }}
    >
      <Typography sx={{ fontSize: PP_LABEL_FS, fontWeight: strong ? 800 : 400, color: strong ? (cur.tone ? ppToneColor(cur.tone) : COLORS.warnStrong) : COLORS.textMuted, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.25, minWidth: 0 }}>{label}</Typography>
      {hasCmp ? <Typography sx={valSx(cmp.tone)}>{cmp.text}</Typography> : null}
      <Typography sx={valSx(cur.tone)}>{cur.text}</Typography>
    </Box>
  )
}

function PpGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: '0.66rem', color: COLORS.textSubtle, mt: 1.5, mb: 0.25, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {children}
    </Typography>
  )
}

/* ---------- comparison PO selector -------------------------------------- */

function ComparisonPoPicker({
  options,
  poMeta,
  value,
  onChange,
}: {
  options: string[]
  poMeta: Props['poMeta']
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
        Compare with
      </Typography>
      <Autocomplete
        size="small"
        options={options}
        value={value || null}
        onChange={(_e, v) => onChange(v ?? '')}
        getOptionLabel={(o) => o || ''}
        isOptionEqualToValue={(a, b) => a === b}
        renderOption={(props, option) => {
          const m = poMeta[option]
          const { key, ...optionProps } = props as typeof props & { key?: string | number }
          return (
            <Box component="li" key={key} {...optionProps} sx={{ display: 'block !important', py: 0.4 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
                {option}
              </Typography>
              {m && (m.bomCode || m.poDate) ? (
                <Typography sx={{ fontSize: '0.66rem', color: COLORS.textMuted, lineHeight: 1.15 }}>
                  {[m.bomCode, m.poDate].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
            </Box>
          )
        }}
        slotProps={{ paper: { sx: { minWidth: 200, '& .MuiAutocomplete-option': { minHeight: 'auto' } } } }}
        sx={{
          width: { xs: 168, sm: 184 },
          // Compact nhưng KHÔNG override `endAdornment top` (để MUI tự căn giữa nút mũi
          // tên, tránh lệch). py nhỏ + icon padding nhỏ; giữ 16px ở xs chống iOS zoom.
          '& .MuiOutlinedInput-root': { py: 0.25 },
          '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.72rem' } },
          '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { p: 0.25 },
        }}
        renderInput={(params) => <TextField {...params} placeholder="Comparison PO…" />}
      />
    </Stack>
  )
}

/* ---------- main tab ----------------------------------------------------- */

export default function ComparisonTab({ factoryCode, poNo, poOptions, poMeta, cmpPo, onCmpPoChange }: Props) {
  // Comparison PO controlled từ BatchReportPage. Mặc định (PO đầu khác current)
  // được hydrate ở parent — tab chỉ đọc/đổi qua onCmpPoChange.
  const setCmpPo = onCmpPoChange

  const { data, isFetching, isError } = useGetBatchReportComparisonQuery(
    { factoryCode, poNo, comparisonPoNo: cmpPo },
    { skip: !poNo },
  )

  // Picker (+ refreshing) — ở state data đặt CÙNG DÒNG title block "So sánh thu hồi
  // của mẻ" (tiết kiệm không gian); loading/error bọc row căn phải.
  const pickerEl = (
    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
      {isFetching ? (
        <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>refreshing…</Typography>
      ) : null}
      <ComparisonPoPicker options={poOptions.filter((p) => p !== poNo)} poMeta={poMeta} value={cmpPo} onChange={setCmpPo} />
    </Stack>
  )

  if (!poNo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Typography sx={{ fontSize: '0.85rem', color: COLORS.textMuted }}>
          Select a PO to view the comparison.
        </Typography>
      </Box>
    )
  }
  if (isError) {
    return (
      <Stack spacing={2}>
        {pickerEl}
        <Alert severity="error" sx={{ m: 1 }}>
          Could not load comparison data for {poNo}. Please retry or check the system connection.
        </Alert>
      </Stack>
    )
  }
  if (!data) {
    return (
      <Stack spacing={2}>
        {pickerEl}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <PriceProductionPanels
        price={data.priceInfo}
        prod={data.productionInfo}
        pnl={data.pnlInfo}
        priceCmp={data.priceInfoCompare}
        prodCmp={data.productionInfoCompare}
        pnlCmp={data.pnlInfoCompare}
        curPo={poNo}
        cmpPo={data.comparisonPoNo}
        headerRight={pickerEl}
      />
      <YieldComparison rows={data.yieldComparison} curPo={poNo} cmpPo={data.comparisonPoNo} />
      <FaqQualityCompare rows={data.faqQuality} />
    </Stack>
  )
}
